import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { StatsChartProps } from '@ui/components/StatsCharts/StatsChart';
import { useTranslations } from '@ui/i18n';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { useMemo } from 'react';

import type { StatsMetricValue, StatsResponseType } from './types';

const endpoint = process.env.NEXT_PUBLIC_STATS_API ?? '';

type ResultType = {
  atomVolume: StatsMetricValue[];
  croVolume: StatsMetricValue[];
  stakeAtom: StatsMetricValue[];
  stakeCro: StatsMetricValue[];
  unstakeAtom: StatsMetricValue[];
  unstakeCro: StatsMetricValue[];
};

async function fetchMetrics(
  startTimestamp: number,
  endTimestamp: number,
): Promise<ResultType> {
  const response: AxiosResponse<StatsResponseType> = await axios.post(
    endpoint,
    {
      endpointUrl: 'insights/cronos/veno/query',
      timeRange: {
        start: startTimestamp.toString(),
        end: endTimestamp.toString(),
        step: 86400,
      },
      queries: [
        {
          eventsQuery: {
            resource: {
              name: 'StakeLcro',
              type: 'EVENTS',
            },
            alias: 'Stake CRO',
            id: 'a',
            aggregation: {
              aggregateProperties: {
                type: 'SUM',
                propertyName: 'croAmount',
              },
            },
            selectorExpr: null,
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: false,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'RequestUnbond',
              type: 'EVENTS',
            },
            alias: 'Unstake LCRO',
            id: 'b',
            aggregation: {
              aggregateProperties: {
                type: 'SUM',
                propertyName: 'lcro_unstaked',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'contract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue: 'LiquidCro',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'RequestUnbond',
              type: 'EVENTS',
            },
            alias: 'LCRO rate',
            id: 'c',
            aggregation: {
              aggregateProperties: {
                type: 'FIRST',
                propertyName: 'liquidCro2CroExchangeRate',
              },
            },
            selectorExpr: null,
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'StakeLatom',
              type: 'EVENTS',
            },
            alias: 'Stake ATOM',
            id: 'd',
            aggregation: {
              aggregateProperties: {
                type: 'SUM',
                propertyName: 'tokenAmount',
              },
            },
            selectorExpr: null,
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: false,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'RequestUnbond',
              type: 'EVENTS',
            },
            alias: 'Unstake LATOM',
            id: 'e',
            aggregation: {
              aggregateProperties: {
                type: 'SUM',
                propertyName: 'latom_unstaked',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'contract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue: 'LiquidAtom',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'RequestUnbond',
              type: 'EVENTS',
            },
            alias: 'LATOM rate',
            id: 'f',
            aggregation: {
              aggregateProperties: {
                type: 'FIRST',
                propertyName: 'liquidToken2TokenExchangeRate',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'contract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue: 'LiquidAtom',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
      ],
      formulas: [
        {
          expression: 'b*c/(10^8)',
          alias: 'Unstake CRO',
          id: 'A',
          disabled: false,
          functions: [],
        },
        {
          expression: 'e*f/(10^8)',
          alias: 'Unstake ATOM',
          id: 'B',
          disabled: false,
          functions: [],
        },
        {
          expression: 'a-b*c/(10^8)',
          alias: 'CRO Volume',
          id: 'C',
          disabled: false,
          functions: [],
        },
        {
          expression: 'd-e*f/(10^8)',
          alias: 'ATOM Volume',
          id: 'D',
          disabled: false,
          functions: [],
        },
      ],
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const results = response.data.results;

  if (results.length < 4) {
    console.error('invalid response from API for useStatsTvlUsd');
  }

  const croVolume =
    results.find((r) => r.id === 'C')?.matrix?.samples[0]?.values ?? [];
  const atomVolume =
    results.find((r) => r.id === 'D')?.matrix?.samples[0]?.values ?? [];
  const unstakeCro =
    results.find((r) => r.id === 'A')?.matrix?.samples[0]?.values ?? [];
  const unstakeAtom =
    results.find((r) => r.id === 'B')?.matrix?.samples[0]?.values ?? [];
  const stakeCro =
    results.find((r) => r.id === 'a')?.matrix?.samples[0]?.values ?? [];
  const stakeAtom =
    results.find((r) => r.id === 'd')?.matrix?.samples[0]?.values ?? [];

  return {
    croVolume,
    atomVolume,
    unstakeCro,
    unstakeAtom,
    stakeCro,
    stakeAtom,
  };
}

export const useStatsAverageDailyVolume = (
  startTimestamp: number,
  endTimestamp: number,
  options?: UseQueryOptions<ResultType>,
): StatsChartProps => {
  const queryKey = ['useStatsAverageDailyVolume', startTimestamp, endTimestamp];
  const t = useTranslations();

  const result = useQuery({
    queryKey,
    queryFn: () => fetchMetrics(startTimestamp, endTimestamp),
    ...options,
  }).data;

  return useMemo(
    () => ({
      title: t('Average Daily Staking Volume'),
      values: {
        [t('{coin} volume', { coin: 'CRO' })]: result?.croVolume,
        [t('{coin} volume', { coin: 'ATOM' })]: result?.atomVolume,
        [t('Stake {coin}', { coin: 'CRO' })]: result?.stakeCro,
        [t('Stake {coin}', { coin: 'ATOM' })]: result?.stakeAtom,
        [t('Unstake {coin}', { coin: 'CRO' })]: result?.unstakeCro,
        [t('Unstake {coin}', { coin: 'ATOM' })]: result?.unstakeAtom,
      },
      type: 'bar',
    }),
    [result, t],
  );
};
